import { MANIA_LANE_COUNT, type ManiaLane } from '@shared/maniaNote';
import { withAlpha } from '../../piano/render/pianoRenderColour';
import { perspectiveProgress, type GuitarLayout } from '../guitarPlayfieldGeometry';
import {
  APPROACH_PULSE_END,
  APPROACH_PULSE_START,
  APPROACH_PULSE_STRENGTH,
  GEM_TRAIL_ALPHA,
  GEM_TRAIL_OFFSET_PROGRESS,
  HOLD_CHEVRON_ALPHA,
  HOLD_CHEVRON_COUNT,
  LANE_GEM_ACCENTS,
  LANE_GEM_COLOURS,
} from './guitarRenderConstants';
import { drawGemShape } from './guitarGemShape';

/**
 * Geometry + visual-effect helpers extracted from {@link
 * import('./guitarDrawNotes').GuitarNoteRenderer} so the renderer
 * class stays focused on "walk events → dispatch to drawGem /
 * drawHold." Everything here is pure (no internal state) and
 * allocation-free in the per-frame hot path.
 *
 * The geometric helpers (`laneCentreXAtProgress`, `laneWidthAtProgress`,
 * `highwayProgressY`) duplicate the math from
 * `guitarPlayfieldGeometry.ts` rather than calling into it — the
 * note renderer hits these per gem (≥ 30 calls per frame on a dense
 * chart), and keeping the math inlined here means the rest of the
 * codebase can read the geometry helpers without paying for a hot-
 * loop function-call cost.
 */

/**
 * Lane centre-x at vertical `progress` (0 = far horizon, 1 = strum
 * bar). The lane stack widens with progress so the centre tracks
 * along the trapezoid.
 */
export function laneCentreXAtProgress(
  layout: GuitarLayout,
  lane: ManiaLane,
  progress: number,
): number {
  const t = Math.max(0, Math.min(1, progress));
  const stackWidth =
    layout.topStackWidth + (layout.bottomStackWidth - layout.topStackWidth) * t;
  const laneWidth =
    layout.topLaneWidth + (layout.bottomLaneWidth - layout.topLaneWidth) * t;
  const stackLeft = layout.centreX - stackWidth / 2;
  const gap = (stackWidth - laneWidth * MANIA_LANE_COUNT) / (MANIA_LANE_COUNT - 1);
  return stackLeft + lane * (laneWidth + gap) + laneWidth / 2;
}

export function laneWidthAtProgress(layout: GuitarLayout, progress: number): number {
  const t = Math.max(0, Math.min(1, progress));
  return layout.topLaneWidth + (layout.bottomLaneWidth - layout.topLaneWidth) * t;
}

export function highwayProgressY(layout: GuitarLayout, progress: number): number {
  const t = Math.max(0, Math.min(1, progress));
  return layout.highwayTopY + (layout.strumBarY - layout.highwayTopY) * t;
}

/**
 * Late-tap "the gem is about to auto-miss" pulse. Returns 0 when the
 * gem is still inside the perfect window or hasn't reached the
 * danger tail yet, otherwise a 0..1 oscillating intensity. Identical
 * vocabulary to Piano's late-pulse so muscle memory ports cleanly.
 */
export function computeDangerPulse(
  lateOffsetBeats: number,
  perfectOverlapBeats: number,
  dangerWindowBeats: number,
): number {
  if (dangerWindowBeats <= 0) {
    return 0;
  }
  const dangerStart = perfectOverlapBeats - dangerWindowBeats;
  if (lateOffsetBeats <= dangerStart || lateOffsetBeats >= perfectOverlapBeats) {
    return 0;
  }
  return 0.5 + 0.5 * Math.sin(performance.now() * 0.035);
}

/**
 * "Lock-in" scale pulse for a gem in the final stretch of its
 * approach. Linear ramp from 0 at `APPROACH_PULSE_START` to 1 at
 * `APPROACH_PULSE_END` (the strum bar). No decay — Guitar Hero's
 * iconic feel is gems accelerating into the bar without any visual
 * "slow down" right before impact, and an old peak-then-decay shape
 * here read as a deceleration to players. Returns 0 outside the
 * window so the gem reads at its normal size during early travel.
 */
export function computeApproachPulse(progress: number): number {
  if (progress <= APPROACH_PULSE_START) {
    return 0;
  }
  if (progress >= APPROACH_PULSE_END) {
    return 1;
  }
  return (progress - APPROACH_PULSE_START) / (APPROACH_PULSE_END - APPROACH_PULSE_START);
}

/**
 * Composite "draw one gem at this point along its lane" — handles
 * perspective placement, lane-colour pickup, entry-fade, approach
 * pulse, trail emission, and the final shape paint. Shared between
 * the tap-gem and hold-head paths so the visual vocabulary stays
 * identical regardless of whether the gem is standalone or attached
 * to a sustain ribbon.
 *
 * Callers compute the gem's `progress` (0 = far horizon, 1 = strum
 * bar) and pass a precomputed `dangerPulse` (0 for hold heads, which
 * have no late-tap window of their own — the ribbon's danger pulse
 * fires instead).
 */
export function drawGemAt(
  ctx: CanvasRenderingContext2D,
  layout: GuitarLayout,
  lane: ManiaLane,
  progress: number,
  isDual: boolean,
  dangerPulse: number,
  lightweightGems: boolean,
  reducedMotion: boolean,
): void {
  if (progress < 0) {
    return;
  }
  // Convert the engine's linear time-progress to perspective-correct
  // screen progress so the gem visually accelerates as it approaches
  // the strum bar (matches Guitar Hero's 3D projection feel). All
  // on-screen geometry (y, lane width, gem scale) and the lock-in
  // approach pulse — which lives in screen-space, see
  // `APPROACH_PULSE_START` — read off this perspective-t.
  const t = perspectiveProgress(progress);
  // Entry fade-in stays tied to LINEAR time so a gem reaches full
  // opacity quickly after spawn rather than lingering dim during the
  // long horizon-crawl portion of the perspective curve. (At
  // perspective-t = 0.18 the gem is already ~40% through its
  // approach time, which would feel mushy.)
  const tLinear = Math.max(0, Math.min(1, progress));
  const y = highwayProgressY(layout, t);
  const laneWidth = laneWidthAtProgress(layout, t);
  const laneCentreX = laneCentreXAtProgress(layout, lane, t);
  const height = laneWidth * 0.55;
  const baseScale = 0.5 + 0.5 * t;
  const entryAlpha = Math.max(0, Math.min(1, tLinear / 0.18));
  const baseColour = LANE_GEM_COLOURS[lane];
  const accentColour = LANE_GEM_ACCENTS[lane];
  const approachPulse = reducedMotion ? 0 : computeApproachPulse(t);
  const visualScale = 1 + approachPulse * APPROACH_PULSE_STRENGTH;
  const renderW = laneWidth * visualScale;
  const renderH = height * visualScale;
  const renderX = laneCentreX - renderW / 2;
  const renderY = y - renderH / 2;

  // Trail is "decoration" — skipped on the low tile-shadows tier so
  // the per-gem cost is just the head shape + outline. On high tier
  // the trail still draws so the desktop polish stays intact.
  if (!reducedMotion && !lightweightGems) {
    drawGemTrail(ctx, layout, lane, t, baseScale, isDual, baseColour, accentColour, entryAlpha);
  }

  ctx.save();
  ctx.globalAlpha = entryAlpha * (1 + approachPulse * 0.15);
  drawGemShape(
    ctx,
    renderX,
    renderY,
    renderW,
    renderH,
    baseColour,
    accentColour,
    isDual,
    baseScale * visualScale,
    dangerPulse,
    lightweightGems,
    reducedMotion,
  );
  ctx.restore();
}

/**
 * Faint trailing-edge copy of the gem placed at a slightly earlier
 * progress along the lane — gives the eye a sense of forward motion
 * even on a dense chart where adjacent gems already imply motion.
 * Always uses the lightweight gem path (flat fill + outline) so the
 * cost stays negligible regardless of quality tier.
 */
export function drawGemTrail(
  ctx: CanvasRenderingContext2D,
  layout: GuitarLayout,
  lane: ManiaLane,
  progress: number,
  perspectiveScale: number,
  isDual: boolean,
  baseColour: string,
  accentColour: string,
  entryAlpha: number,
): void {
  const trailProgress = progress - GEM_TRAIL_OFFSET_PROGRESS;
  if (trailProgress <= 0) {
    return;
  }
  const trailY = highwayProgressY(layout, trailProgress);
  const trailLaneWidth = laneWidthAtProgress(layout, trailProgress);
  const trailCentreX = laneCentreXAtProgress(layout, lane, trailProgress);
  const trailH = trailLaneWidth * 0.55;
  const trailScale = (0.5 + 0.5 * trailProgress) * 0.88;
  ctx.save();
  ctx.globalAlpha = entryAlpha * GEM_TRAIL_ALPHA;
  drawGemShape(
    ctx,
    trailCentreX - trailLaneWidth / 2,
    trailY - trailH / 2,
    trailLaneWidth,
    trailH,
    baseColour,
    accentColour,
    isDual,
    perspectiveScale * 0.88,
    0,
    true,
    true,
  );
  // Subtle accent-coloured wash on top so the trail still reads as
  // the same lane colour and not a generic dim ghost.
  ctx.globalAlpha = entryAlpha * GEM_TRAIL_ALPHA * 0.45;
  ctx.fillStyle = accentColour;
  ctx.fillRect(
    trailCentreX - (trailLaneWidth * trailScale) / 2,
    trailY - trailH / 2,
    trailLaneWidth * trailScale,
    trailH,
  );
  ctx.restore();
}

/**
 * Flowing chevron stream rendered on top of an engaged hold ribbon.
 * Caller picks {@link HOLD_CHEVRON_COUNT} on high tier and a halved
 * count on low so the per-frame path-fill cost on a busy hold-heavy
 * chart stays bounded on phones. Sine-bell alpha so chevrons fade
 * at both ends of the ribbon and never clip the head / tail.
 */
export function drawHoldChevronStream(
  ctx: CanvasRenderingContext2D,
  phase: number,
  tailY: number,
  headY: number,
  tailCentreX: number,
  headCentreX: number,
  tailRibbonW: number,
  headRibbonW: number,
  count: number = HOLD_CHEVRON_COUNT,
): void {
  ctx.save();
  for (let i = 0; i < count; i += 1) {
    const p = (phase + i / count) % 1;
    const cx = tailCentreX + (headCentreX - tailCentreX) * p;
    const cy = tailY + (headY - tailY) * p;
    const ribbonW = tailRibbonW + (headRibbonW - tailRibbonW) * p;
    const chevronW = ribbonW * 0.6;
    const chevronH = ribbonW * 0.4;
    const alpha = Math.sin(p * Math.PI) * HOLD_CHEVRON_ALPHA;
    if (alpha <= 0.02) {
      continue;
    }
    ctx.fillStyle = withAlpha('#ffffff', alpha);
    ctx.beginPath();
    ctx.moveTo(cx - chevronW / 2, cy - chevronH / 2);
    ctx.lineTo(cx + chevronW / 2, cy - chevronH / 2);
    ctx.lineTo(cx, cy + chevronH / 2);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}
